import type { BlotterOrderEntry } from '@/store/blotter/types/BlotterOrderEntry.ts';
import { getOMSCurrencyFromBlotterCurrency } from '@/store/orders/helpers.ts';
import type { ClientWay, OrderStateCurrencyPair } from '@/store/orders/types.ts';
import type { Order, OrderMode } from '@/store/types.ts';
import type { OmsErrors, OmsWarning } from '@/types/common.ts';
import type { Currency } from '@/types/currency.ts';
import type { ISODate, StringTime } from '@/types/date.ts';

export type FixingOrder = Order<'FixingOrder', FixingOrderProperties>;

export type FixingOrderProperties = {
  status:
    | { type: 'creating' }
    | { type: 'pending' }
    | { type: 'failed'; errorMessage: string }
    | { type: 'submitted' | 'editing' | 'cancelled'; tradeId: string };

  clientWay: ClientWay;
  notional?: number;
  notionalCurrencyIndex: 1 | 2;

  currencyPair?: OrderStateCurrencyPair;

  fixingReference: string;
  fixingPlace?: string;
  fixingTime?: string;
  fixingPriceType: FixingPriceType;
  fixingDateUtc?: string;
  expiryDay?: string;
  commercialMargin?: number;
  fixingMarginType: FixingMarginType;

  fixingTimesOfSelectedType: string[];
  fixingTypes: string[];
  sourceNameOfSelectedType: string[];
  canModifyMarginInBps: boolean;
  isStandardGroup: boolean;
  isCcyForcedBidAsk: boolean;

  notificationEmails?: string[];

  isReadyToSubmit: boolean;
  clockOffset?: number; // offset in milliseconds between the backend and the client

  // to detect if the order is in EDIT or VIEW mode
  // EDIT mode is used when the user is editing an existing order
  // VIEW mode is used when the user is viewing an existing order
  mode: OrderMode;

  errors?: OmsErrors;
  warnings?: OmsWarning;
};

export type FixingOrderError = {
  field?: FixingOrderFieldName;
  message: string;
  code: number;
};

export enum FixingOrderFieldName {
  id = 'id',
  currencyPair = 'currencyPair',
  bdrId = 'bdrId',
  way = 'way',
  notional = 'notional',
  currency = 'currency',
  limitPrice = 'limitPrice',
  expiryDate = 'expiryDate',
  trigger = 'trigger',
  notificationEmails = 'notificationEmails',
}

export const triggers = ['Spot', 'BidOffer', 'RateOneTouch'] as const;
export type Trigger = (typeof triggers)[number];

export type GoodTillCancelled = {
  type: 'GoodTillCancelled';
};

export type ExpiryDate = {
  type: 'ExpiryDate';
  date?: ISODate;
  time?: StringTime;
};

export const generateFixingOrder = (): FixingOrder => {
  return {
    type: 'FixingOrder',
    properties: {
      status: { type: 'creating' },
      clientWay: 'buy',
      notional: undefined,
      notionalCurrencyIndex: 1,
      currencyPair: undefined,

      fixingReference: '',
      fixingPlace: undefined,
      fixingTime: undefined,
      fixingPriceType: 'MID',
      fixingDateUtc: undefined,
      commercialMargin: undefined,
      expiryDay: undefined,
      fixingMarginType: 'MarginInBps',
      canModifyMarginInBps: true,
      isStandardGroup: false,
      isCcyForcedBidAsk: false,

      fixingTimesOfSelectedType: [],
      sourceNameOfSelectedType: [],
      fixingTypes: [],

      notificationEmails: undefined,
      errors: {},
      isReadyToSubmit: false,
      clockOffset: 0,
      mode: 'VIEW',
    },
  };
};

export const generateFixingOrderFromBlotter = (entry: BlotterOrderEntry, mode: OrderMode): FixingOrder => {
  const currencyPair = getOMSCurrencyFromBlotterCurrency(entry.currencyPair);
  return {
    id: entry.id,
    type: 'FixingOrder',
    clientId: undefined,

    properties: {
      mode,
      currencyPair,
      status: { type: 'submitted', tradeId: entry.id },
      notificationEmails: undefined,
      isReadyToSubmit: false,

      clientWay: entry.yourWay === 'Buy' ? 'buy' : 'sell',

      notional: entry.notional ?? undefined,
      notionalCurrencyIndex: entry.notionalCurrency === currencyPair?.currency1 ? 1 : 2,

      fixingReference: entry.fixingBenchmark ?? '',
      fixingPlace: entry.fixingPlace ?? undefined,
      fixingTime: entry.fixingTime ?? undefined,
      fixingPriceType: (entry.fixingPriceType ?? 'MID') as FixingPriceType,
      fixingDateUtc: entry.fixingDateUtc ?? undefined,

      fixingMarginType: (entry.fixingMarginType ?? 'MarginInBps') as FixingMarginType,
      commercialMargin: entry.marginInBps ?? undefined,

      expiryDay: entry.expiryDay ?? undefined,

      canModifyMarginInBps: false,
      isStandardGroup: false,
      isCcyForcedBidAsk: false,

      fixingTimesOfSelectedType: [],
      sourceNameOfSelectedType: [],
      fixingTypes: [],

      clockOffset: 0,
    },
  };
};

export type ValidateFixingOrderRequest = {
  tileId: string;
  clientId: number;
  way?: ClientWay;
  notional?: number;
  notionalCurrencyIndex: 1 | 2;
  currency1: Currency;
  currency2: Currency;

  fixingReference: string; // fixingBenchmark
  fixingPlace?: string;
  fixingTime?: string;
  fixingPriceType: FixingPriceType;
  fixingDateUtc?: string;
  expiryDay?: string;
  commercialMargin?: number;
  fixingMarginType: FixingMarginType;

  notificationEmails?: {
    isOverridenEmails?: boolean;
    emails: string[];
  };
  clockOffset?: number; // offset in milliseconds between the backend and the client
};

export type SubmitFixingOrderRequest = {
  tileId: string;
  clientId: number;
  way: ClientWay;
  notional: number;
  notionalCurrencyIndex: 1 | 2;
  currency1: Currency;
  currency2: Currency;

  fixingReference: string;
  fixingPlace?: string;
  fixingTime?: string;
  fixingPriceType: FixingPriceType;
  fixingDateUtc: string | undefined;
  expiryDay: string | undefined;
  commercialMargin: number | undefined;
  fixingMarginType: FixingMarginType;

  notificationEmails?: string[];
  clockOffset?: number; // offset in milliseconds between the backend and the client
};

export const mapFixing = (
  product: {
    type: 'FixingOrder';
    clientId?: number;
    properties: FixingOrderProperties;
    tileId: string;
  },
  defaultClientId: number,
): ValidateFixingOrderRequest => {
  return {
    tileId: product.tileId,
    currency1: product.properties.currencyPair?.currency1 ?? '',
    currency2: product.properties.currencyPair?.currency2 ?? '',
    clientId: product.clientId ?? defaultClientId,
    way: product.properties.clientWay,
    notional: product.properties.notional,
    fixingReference: product.properties.fixingReference,
    fixingPlace: product.properties.fixingPlace,
    fixingTime: product.properties.fixingTime,
    fixingPriceType: product.properties.fixingPriceType,
    fixingDateUtc: product.properties.fixingDateUtc,
    expiryDay: product.properties.expiryDay,
    commercialMargin: product.properties.commercialMargin,
    fixingMarginType: product.properties.fixingMarginType,
    notionalCurrencyIndex: product.properties.notionalCurrencyIndex,
    notificationEmails: {
      isOverridenEmails: false,
      emails: product.properties.notificationEmails ?? [],
    },
    clockOffset: 0,
  };
};

export const fixingPriceTypes = ['MID', 'BIDASK'] as const;
export type FixingPriceType = (typeof fixingPriceTypes)[number];

export const fixingMarginTypes = ['BidAskMidSpread', 'MarginInBps'] as const;
export type FixingMarginType = (typeof fixingMarginTypes)[number];
